import { exists, getKeys, isPrimitive } from '@jack-henry/frontend-utils/functions';
import { camelCaseToTitle, noOp } from '@treasury/utils';
import { DeepRequired } from '@treasury/utils/types';
import { nothing } from 'lit';
import {
    TmTableConfigMultiNormalized,
    TmTableConfigNormalized,
    TmTableOptionsNormalized,
    TmTableViewNormalized,
} from './tm-table-internal.types';
import { DEFAULT_REDUCER, isMultiConfig } from './tm-table.helpers';
import {
    type InitialSort,
    NoPagination,
    TmTableAction,
    TmTableColsDef,
    TmTableColumn,
    TmTableConfig,
    TmTableConfigMulti,
    TmTableFilter,
    TmTableOptions,
    TmTablePaginationConfig,
    TmTableRowAction,
    TmTableSelectAction,
    TmTableSelectCustomDef,
    TmTableView,
} from './tm-table.types';

const DEFAULT_SEARCH_PLACEHOLDER = 'Search...';
const DEFAULT_NO_CONTENT = 'No results';

/**
 * Take a partial `TmTableOptions` provided by an external consumer
 * and normalize it to a full config, filling in missing values with sensible defaults.
 *
 * @param sampleItem: A sample item to derive schema information from.
 */
export function normalizeOptions<T extends object>(
    options: TmTableOptions<T> = {},
    sampleItem?: T
): TmTableOptionsNormalized<T> {
    if (!options) {
        return normalizeSingleConfig(undefined, sampleItem);
    }

    if (isMultiConfig(options)) {
        return normalizeMultiConfig<T>(options, sampleItem);
    } else {
        return normalizeSingleConfig(options, sampleItem);
    }
}

function normalizePagination<T extends object>(
    pagination?: TmTablePaginationConfig<T> | typeof NoPagination
): Required<TmTablePaginationConfig<T>> | typeof NoPagination {
    if (pagination === NoPagination) {
        return NoPagination;
    }

    const pageLimits = pagination?.pageLimits?.filter(exists) || [10, 25, 50, 100];

    return {
        minQueryLength: 2,
        pageLimits,
        getPage: noOp,
        ...pagination,
    };
}

function normalizeSingleConfig<T extends object>(
    config?: TmTableConfig<T>,
    sampleItem?: T
): TmTableConfigNormalized<T> {
    const columns = normalizeColsDef(config?.columns || sampleItem);
    const { initialActive } = config || {};
    const normalized: TmTableConfigNormalized<T> = {
        subtitle: '',
        allowMultiSelect: false,
        allowActiveRow: exists(initialActive),
        defaultQuery: '',
        noContentMessage: DEFAULT_NO_CONTENT,
        searchPlaceholder: DEFAULT_SEARCH_PLACEHOLDER,
        refreshData: noOp,
        reduceData: DEFAULT_REDUCER,
        ...config,
        selectActions: normalizeSelectActions(config?.selectActions),
        tableActions: normalizeTableActions(config?.tableActions),
        rowActions: normalizeRowActions(config?.rowActions),
        pagination: normalizePagination(config?.pagination),
        filters: normalizeFilters(config?.filters),
        columns,
        initialSort: normalizeInitialSort(config?.initialSort, columns.definitions),
    };

    if (exists(initialActive) && !normalized.allowActiveRow) {
        throw new Error('Provided an initial active row item but allowActiveRow was set to false.');
    }

    return normalized;
}

function normalizeRowActions<T extends object>(
    rowActions?: TmTableRowAction<T>[]
): DeepRequired<TmTableRowAction<T>>[] {
    if (!rowActions) {
        return [] as DeepRequired<TmTableRowAction<T>>[];
    }

    return rowActions.map(action => ({
        isHidden: false,
        isDisabled: false,
        icon: '',
        ...action,
    }));
}

function normalizeSelectActions<T>(
    selectActionsOrCustom?: TmTableSelectAction<T>[] | TmTableSelectCustomDef<T>
): DeepRequired<TmTableSelectAction<T>>[] | TmTableSelectCustomDef<T> {
    if (!selectActionsOrCustom) {
        return [] as DeepRequired<TmTableSelectAction<T>>[];
    }

    if (Array.isArray(selectActionsOrCustom)) {
        return selectActionsOrCustom.map(action => ({
            icon: '',
            ...action,
        }));
    }

    return selectActionsOrCustom;
}

function normalizeMultiConfig<T extends object>(
    config: TmTableConfigMulti<T>,
    sampleItem?: T
): TmTableConfigMultiNormalized<T> {
    const { displayAllView, views } = config;
    return {
        displayAllView: exists(displayAllView) ? displayAllView : false,
        views: normalizeViews(views, sampleItem),
    };
}

function normalizeTableActions<T extends object>(actions?: TmTableAction<T>[]) {
    if (!actions || actions.length < 1) {
        return [];
    }

    return actions.map(a => ({
        icon: '',
        label: '',
        tooltip: '',
        ...a,
    }));
}

function normalizeColsDef<T extends object>(
    colsDefOrSampleItem?: T | TmTableColsDef<T>
): DeepRequired<TmTableColsDef<T>> {
    if (!exists(colsDefOrSampleItem)) {
        return {
            canEdit: false,
            definitions: [],
        };
    }

    const isColsDef = (obj: T | TmTableColsDef<T>): obj is TmTableColsDef<T> =>
        'definitions' in obj;
    const colsOrSampleItem = isColsDef(colsDefOrSampleItem)
        ? colsDefOrSampleItem.definitions
        : colsDefOrSampleItem;

    const definitions = normalizeColumns(colsOrSampleItem);
    const colsDef = isColsDef(colsDefOrSampleItem) ? colsDefOrSampleItem : undefined;

    return {
        canEdit: false,
        ...colsDef,
        definitions,
    };
}

function normalizeColumns<T extends object>(
    colsOrSampleItem?: T | TmTableColumn<T>[]
): DeepRequired<TmTableColumn<T>>[] {
    if (!colsOrSampleItem) {
        return [] as DeepRequired<TmTableColumn<T>>[];
    }

    if (!Array.isArray(colsOrSampleItem)) {
        return createDefaultColumns(colsOrSampleItem);
    }

    return colsOrSampleItem.map(col => ({
        searchable: true,
        sortable: true,
        alignment: 'left',
        dataType: 'string',
        label: '',
        render: defaultColRenderer,
        ...col,
    }));
}

function normalizeInitialSort<T extends object>(
    initialSort: InitialSort<T> | undefined,
    cols: TmTableColumn<T>[]
) {
    if (cols.length < 1) {
        return {
            prop: '' as never,
            direction: 'asc' as const,
        };
    }

    if (initialSort) {
        return initialSort;
    }

    const firstSortable = cols.find(c => c.sortable);
    if (!firstSortable) {
        return {
            prop: '' as never,
            direction: 'asc' as const,
        };
    }

    return {
        prop: firstSortable.prop,
        direction: 'asc' as const,
    };
}

function normalizeViews<T extends object>(
    views: TmTableView<T>[],
    sampleItem?: T
): TmTableViewNormalized<T>[] {
    return views.map(v => {
        const { filters, pagination, initialActive } = v;
        const columns = v.columns
            ? normalizeColsDef(v.columns)
            : {
                  definitions: normalizeColumns(sampleItem),
                  canEdit: false,
              };

        const normalized: TmTableViewNormalized<T> = {
            allowMultiSelect: false,
            allowActiveRow: exists(initialActive),
            noContentMessage: DEFAULT_NO_CONTENT,
            searchPlaceholder: DEFAULT_SEARCH_PLACEHOLDER,
            subtitle: '',
            defaultQuery: '',
            onClick: noOp,
            refreshData: noOp,
            reduceData: DEFAULT_REDUCER,
            renderSubheader: () => nothing,
            ...v,
            selectActions: normalizeSelectActions(v?.selectActions),
            tableActions: normalizeTableActions(v?.tableActions),
            rowActions: normalizeRowActions(v?.rowActions),
            pagination: normalizePagination(pagination),
            columns,
            filters: normalizeFilters(filters),
            initialSort: normalizeInitialSort(v?.initialSort, columns.definitions),
        };

        if (exists(initialActive) && !normalized.allowActiveRow) {
            throw new Error(
                'Provided an initial active row item but allowActiveRow was set to false.'
            );
        }

        return normalized;
    });
}

function normalizeFilters<T extends object>(
    filters?: TmTableFilter<T>[]
): DeepRequired<TmTableFilter<T>>[] {
    return filters || [];
}

/**
 * Given a sample item for a table's schema, generate a default list of column definitions.
 *
 * Columns containing objects or other non-serializable data will not be included.
 *
 * @param sampleItem An item representative of the table's column schema.
 * @returns A list of default columns for each property that can be shown as a string in a table cell.
 */
function createDefaultColumns<T extends object>(sampleItem: T): DeepRequired<TmTableColumn<T>>[] {
    /**
     * The <jha-advanced-table> can't search on or display nested objects,
     * so only allow primitive values through for default column definitions.
     */
    const primitiveKeys = getKeys(sampleItem as T).filter(k =>
        isPrimitive(sampleItem[k])
    ) as DeepRequired<keyof T>[];

    return primitiveKeys.map(prop => ({
        prop,
        label: camelCaseToTitle(prop.toString()),
        render: defaultColRenderer,
        dataType: 'string',
        alignment: 'left',
        sortable: true,
        searchable: true,
    }));
}

export function defaultColRenderer(value: unknown) {
    if (isPrimitive(value) || Array.isArray(value)) {
        return exists(value) ? value.toString() : '';
    }

    return value + '';
}
